import { useState } from "react";
import { useIntercom } from "react-use-intercom";

import RestrictedComponent from "common/components/RestrictedComponent";
import {
  ManageSubscriptionDialog,
  SubscriptionDetailsCard,
} from "common/components/subscriptions";
import { Button } from "common/components/ui/Button";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useEstimateRenewal from "common/datahooks/useEstimateRenewal";
import { formatPrice, formatPricingPeriodInfo } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import NonRenewingTag from "../NonRenewingTag";
import Price from "../Price";
import RemoveChangeButton from "../RemoveChangeButton";
import SubscriptionInfo from "../SubscriptionInfo";
import Accounts from "./Accounts";
import ScheduledChangeTag from "./ScheduledChangeTag";

export default function Rented() {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);
  const { showNewMessage } = useIntercom();

  const { rental_subscription: rentedSubscription } =
    useSelectedWorkspaceContext();

  const { estimateRenewal, isLoadingEstimateRenewal } =
    useEstimateRenewal(rentedSubscription);

  let priceValue: string;
  let priceSubtext: string;

  let tagComponent = <Tag variant="disabled">Inactive</Tag>;
  let buttonComponent = (
    <Button onClick={() => setIsOpen(true)} size={isDesktop ? "sm" : "md"}>
      Activate subscription
    </Button>
  );

  if (rentedSubscription) {
    const {
      status,
      has_changes: hasChanges,
      item_price: itemPrice,
    } = rentedSubscription;

    const isSubscriptionCancelled = status === "cancelled";
    const isSubscriptionNonRenewing = status === "non_renewing";

    if (isSubscriptionCancelled) {
      buttonComponent = (
        <Button
          onClick={() =>
            itemPrice.period === 6
              ? showNewMessage("Hey,\nI would like to update my subscription")
              : setIsOpen(true)
          }
          size={isDesktop ? "sm" : "md"}
        >
          Reactivate subscription
        </Button>
      );
    } else {
      if (isSubscriptionNonRenewing) {
        buttonComponent = (
          <RemoveChangeButton
            subscription={rentedSubscription}
            type="cancellation"
          />
        );

        tagComponent = <NonRenewingTag />;
      } else if (hasChanges) {
        buttonComponent = (
          <RemoveChangeButton subscription={rentedSubscription} type="change" />
        );
        tagComponent = <ScheduledChangeTag />;
      } else {
        buttonComponent = (
          <Button
            onClick={() => setIsOpen(true)}
            size={isDesktop ? "sm" : "md"}
          >
            Manage subscription
          </Button>
        );
        tagComponent =
          itemPrice.period === 6 ? (
            <Tag variant="gradient">6 months deal</Tag>
          ) : (
            <Tag variant="success">Active</Tag>
          );
      }
    }
  }

  const isFree =
    !estimateRenewal?.amountDue ||
    !rentedSubscription ||
    rentedSubscription.status === "cancelled";

  if (!isFree) {
    priceValue = formatPrice(estimateRenewal.amountDue / 100);
    priceSubtext = formatPricingPeriodInfo(rentedSubscription.item_price);
  }

  const priceComponent = (
    <Price
      priceValue={priceValue}
      priceSubtext={priceSubtext}
      isLoading={isLoadingEstimateRenewal}
    />
  );

  return (
    <>
      <SubscriptionDetailsCard
        className="relative before:absolute before:size-full before:left-0 before:rounded-3xl before:top-4 before:-z-10 before:blur-[9px] before:bg-[conic-gradient(from_180deg_at_50%_50%,#FDE8FF_0deg,#E0E1FF_54.48deg,#DAF5FF_148.92deg,#FFF8DB_220.96deg,#FFF0E0_291.98deg,#FDE8FF_360deg)]"
        plan="avatar"
        footerComponent={
          <SubscriptionInfo
            nextBillingAt={estimateRenewal?.nextBillingAt}
            isLoading={isLoadingEstimateRenewal}
            subscription={rentedSubscription}
            buttonComponent={
              <RestrictedComponent visibleForPermissions={["billing_full"]}>
                {buttonComponent}
              </RestrictedComponent>
            }
          />
        }
        tagComponent={tagComponent}
        priceComponent={priceComponent}
        mainComponent={rentedSubscription && <Accounts />}
      />
      <ManageSubscriptionDialog
        plan="avatar"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </>
  );
}
